import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { salesAPI, productsAPI, favoritesAPI, customChannelsAPI, etsyAPI, eventsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { QuickAddProduct } from '../components/sales/QuickAdd';
import { 
  Plus, 
  DollarSign, 
  ShoppingCart,
  Trash2,
  Calendar,
  MapPin,
  TrendingUp,
  Package,
  CreditCard,
  Banknote,
  X,
  Store,
  Globe,
  ShoppingBag,
  Building2,
  Percent,
  Settings,
  Star,
  Gift,
  History,
  Filter,
  Layers,
  Hammer,
  Users,
  Palette,
  Truck,
  Heart,
  Camera,
  Music,
  BookOpen,
  Coffee,
  Scissors,
  Pen,
  Phone,
  Mail,
  Tag,
  Award,
  Zap,
  Target,
  Compass,
  Briefcase,
  Home,
  Sun,
  Moon,
  Edit,
  PlusCircle,
  RefreshCw,
  Loader2,
  Download,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { formatDate, formatCurrency } from '../lib/utils';
import { HelpButton } from '../components/HelpButton';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash', icon: Banknote },
  { value: 'card', label: 'Credit/Debit Card', icon: CreditCard },
  { value: 'venmo', label: 'Venmo', icon: DollarSign },
  { value: 'paypal', label: 'PayPal', icon: DollarSign },
  { value: 'square', label: 'Square', icon: CreditCard },
  { value: 'other', label: 'Other', icon: DollarSign },
];

// Built-in channels (cannot be modified)
const BUILTIN_CHANNELS = [
  { value: 'event', label: 'Event/Craft Fair', icon: Store, color: 'blue' },
  { value: 'etsy', label: 'Etsy', icon: ShoppingBag, color: 'orange' },
  { value: 'boutique', label: 'Boutique/Consignment', icon: Building2, color: 'purple' },
  { value: 'website', label: 'Personal Website', icon: Globe, color: 'green' },
  { value: 'farmers_market', label: 'Farmers Market', icon: Store, color: 'teal' },
  { value: 'wholesale', label: 'Wholesale Order', icon: Package, color: 'indigo' },
  { value: 'other', label: 'Other', icon: DollarSign, color: 'gray' },
];

// Icon mapping for custom channels
const ICON_MAP = {
  star: Star,
  gift: Gift,
  hammer: Hammer,
  users: Users,
  palette: Palette,
  truck: Truck,
  heart: Heart,
  camera: Camera,
  music: Music,
  book: BookOpen,
  coffee: Coffee,
  scissors: Scissors,
  pen: Pen,
  phone: Phone,
  mail: Mail,
  tag: Tag,
  award: Award,
  zap: Zap,
  target: Target,
  compass: Compass,
  briefcase: Briefcase,
  home: Home,
  sun: Sun,
  moon: Moon,
};

// Color classes for channels
const COLOR_CLASSES = {
  gray: { bg: 'bg-gray-100', text: 'text-gray-700', iconBg: 'bg-gray-500' },
  red: { bg: 'bg-red-100', text: 'text-red-700', iconBg: 'bg-red-500' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-700', iconBg: 'bg-orange-500' },
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-700', iconBg: 'bg-yellow-500' },
  green: { bg: 'bg-green-100', text: 'text-green-700', iconBg: 'bg-green-500' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-700', iconBg: 'bg-teal-500' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-700', iconBg: 'bg-blue-500' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-700', iconBg: 'bg-indigo-500' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-700', iconBg: 'bg-purple-500' },
  pink: { bg: 'bg-pink-100', text: 'text-pink-700', iconBg: 'bg-pink-500' },
};

const DEFAULT_CHANNEL_FEES = {
  etsy: [
    { fee_type: 'listing', description: 'Listing fee', amount: 0.20, is_percentage: false },
    { fee_type: 'transaction', description: 'Transaction fee (6.5%)', amount: 6.5, is_percentage: true },
    { fee_type: 'processing', description: 'Payment processing (3%)', amount: 3.0, is_percentage: true },
  ],
  boutique: [
    { fee_type: 'commission', description: 'Store commission (40%)', amount: 40, is_percentage: true },
  ],
  website: [
    { fee_type: 'processing', description: 'Payment processing (2.9%)', amount: 2.9, is_percentage: true },
  ],
};

export const Sales = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const [customChannels, setCustomChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isChannelDialogOpen, setIsChannelDialogOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  
  // Advanced filter state
  const [dateRangeFilter, setDateRangeFilter] = useState({ start: '', end: '' });
  const [productFilter, setProductFilter] = useState('all');
  const [groupByEvent, setGroupByEvent] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Helper to extract error message from API response
  const getErrorMessage = (error, fallback = 'An error occurred') => {
    const errorDetail = error.response?.data?.detail;
    if (typeof errorDetail === 'string') return errorDetail;
    if (Array.isArray(errorDetail)) {
      return errorDetail.map(e => e.msg || e.message || 'Validation error').join(', ');
    }
    if (errorDetail?.msg) return errorDetail.msg;
    return fallback;
  };
  
  // Etsy tab state
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'sales');
  const [etsyOrders, setEtsyOrders] = useState([]);
  const [etsyStatus, setEtsyStatus] = useState(null);
  const [etsyLoading, setEtsyLoading] = useState(false);
  const [etsySyncing, setEtsySyncing] = useState(false);
  const [etsySummary, setEtsySummary] = useState(null);
  const [importingOrderId, setImportingOrderId] = useState(null);
  
  // Series events state (for selecting which event in a series to assign sale to)
  const [seriesEvents, setSeriesEvents] = useState([]);
  const [seriesName, setSeriesName] = useState('');
  
  // Quick Add Product dialog state
  const [showQuickAddProduct, setShowQuickAddProduct] = useState(false);
  
  const [formData, setFormData] = useState({
    event_id: '',
    sale_date: new Date().toISOString().split('T')[0],
    sale_type: 'individual',
    sales_channel: 'event',
    items: [],
    payment_methods: [],
    channel_fees: [],
    boutique_name: '',
    order_number: '',
    notes: '',
    deduct_inventory: false,
  });
  
  const [channelFormData, setChannelFormData] = useState({
    name: '',
    icon: 'star',
    color: 'gray',
    description: '',
    default_fees: [],
  });
  
  const [newFee, setNewFee] = useState({ description: '', amount: '', is_percentage: false });
  
  const [newItem, setNewItem] = useState({ product_id: '', quantity: 1, unit_price: '' });
  const [newPayment, setNewPayment] = useState({ method: 'cash', amount: '' });

  // Combine built-in and custom channels
  const allChannels = [
    ...BUILTIN_CHANNELS,
    ...customChannels.map(ch => ({
      value: `custom_${ch.id}`,
      label: ch.name,
      icon: ICON_MAP[ch.icon] || Star,
      color: ch.color || 'gray',
      isCustom: true,
      default_fees: ch.default_fees || [],
      id: ch.id,
    }))
  ];

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
    fetchEtsyData();
  }, [user, authLoading, navigate]);

  // Handle query params to pre-populate form
  useEffect(() => {
    const eventIdParam = searchParams.get('event_id');
    const channelParam = searchParams.get('channel');
    const seriesIdParam = searchParams.get('series_id');
    
    if (eventIdParam || channelParam) {
      setFormData(prev => ({
        ...prev,
        event_id: eventIdParam || prev.event_id,
        sales_channel: channelParam || prev.sales_channel,
      }));
      // Open the dialog if event_id is provided
      if (eventIdParam) {
        setIsDialogOpen(true);
      }
    }
    
    // If series_id is provided, fetch series events for the selector
    if (seriesIdParam && eventIdParam) {
      const fetchSeriesEvents = async () => {
        try {
          const historyRes = await eventsAPI.getSeriesHistory(eventIdParam);
          if (historyRes.data.has_series && historyRes.data.series) {
            const series = historyRes.data.series;
            setSeriesName(series.name);
            // Sort occurrences by date (newest first)
            const sortedEvents = [...series.occurrences].sort((a, b) => 
              (b.date || '').localeCompare(a.date || '')
            );
            setSeriesEvents(sortedEvents);
          }
        } catch (error) {
          console.error('Failed to fetch series events:', error);
        }
      };
      fetchSeriesEvents();
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [salesRes, productsRes, eventsRes, channelsRes] = await Promise.all([
        salesAPI.getAll(),
        productsAPI.getAll(),
        favoritesAPI.getAll(),
        customChannelsAPI.getAll(),
      ]);
      setSales(salesRes.data);
      setProducts(productsRes.data);
      setEvents(eventsRes.data);
      setCustomChannels(channelsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchEtsyData = async () => {
    try {
      setEtsyLoading(true);
      const [statusRes, ordersRes] = await Promise.all([
        etsyAPI.getStatus(),
        etsyAPI.getOrders({ limit: 100 }),
      ]);
      setEtsyStatus(statusRes.data);
      setEtsyOrders(ordersRes.data.orders || []);
      setEtsySummary(ordersRes.data.summary || null);
    } catch (error) {
      console.error('Failed to fetch Etsy data');
    } finally {
      setEtsyLoading(false);
    }
  };

  const handleSyncEtsy = async () => {
    try {
      setEtsySyncing(true);
      const res = await etsyAPI.sync();
      toast.success(res.data.message);
      // Refresh Etsy data
      await fetchEtsyData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to sync Etsy orders'));
    } finally {
      setEtsySyncing(false);
    }
  };

  const handleImportEtsyOrder = async (orderId) => {
    try {
      setImportingOrderId(orderId);
      const res = await etsyAPI.importToSales(orderId);
      toast.success(res.data.message);
      // Refresh sales data
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to import order'));
    } finally {
      setImportingOrderId(null);
    }
  };

  const handleAddItem = () => {
    if (!newItem.product_id || !newItem.quantity) {
      toast.error('Select a product and quantity');
      return;
    }
    
    const product = products.find(p => p.id === newItem.product_id);
    if (!product) return;
    
    const price = newItem.unit_price || product.retail_price || 0;
    
    setFormData({
      ...formData,
      items: [...formData.items, {
        product_id: newItem.product_id,
        product_name: product.name,
        quantity: parseInt(newItem.quantity),
        unit_price: parseFloat(price),
      }]
    });
    setNewItem({ product_id: '', quantity: 1, unit_price: '' });
  };

  // Handle quick add product creation
  const handleQuickProductCreated = (newProduct) => {
    setProducts(prev => [...prev, newProduct]);
    // Auto-select the new product
    setNewItem({
      product_id: newProduct.id,
      quantity: 1,
      unit_price: newProduct.retail_price?.toString() || ''
    });
    toast.success(`"${newProduct.name}" added - you can now add it to this sale`);
  };

  const handleRemoveItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const handleAddPayment = () => {
    if (!newPayment.method || !newPayment.amount) {
      toast.error('Select payment method and amount');
      return;
    }
    
    setFormData({
      ...formData,
      payment_methods: [...formData.payment_methods, {
        method: newPayment.method,
        amount: parseFloat(newPayment.amount)
      }]
    });
    setNewPayment({ method: 'cash', amount: '' });
  };

  const handleRemovePayment = (index) => {
    setFormData({
      ...formData,
      payment_methods: formData.payment_methods.filter((_, i) => i !== index)
    });
  };

  const calculateTotals = () => {
    const itemsTotal = formData.items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
    const paymentsTotal = formData.payment_methods.reduce((sum, pm) => sum + pm.amount, 0);
    
    // Calculate fees
    let feesTotal = 0;
    for (const fee of formData.channel_fees) {
      if (fee.is_percentage) {
        feesTotal += itemsTotal * (fee.amount / 100);
      } else {
        feesTotal += fee.amount;
      }
    }
    
    return { itemsTotal, paymentsTotal, feesTotal, difference: itemsTotal - paymentsTotal };
  };

  const handleChannelChange = (channel) => {
    // Set default fees for the channel
    let defaultFees = DEFAULT_CHANNEL_FEES[channel] || [];
    
    // Check if it's a custom channel
    if (channel.startsWith('custom_')) {
      const customChannel = allChannels.find(c => c.value === channel);
      if (customChannel && customChannel.default_fees) {
        defaultFees = [...customChannel.default_fees];
      }
    }
    
    setFormData({
      ...formData,
      sales_channel: channel,
      channel_fees: [...defaultFees],
      // Clear event_id if not an event channel
      event_id: (channel === 'event' || channel === 'farmers_market') ? formData.event_id : '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Event required only for event-based channels
    const eventChannels = ['event', 'farmers_market'];
    if (eventChannels.includes(formData.sales_channel) && !formData.event_id) {
      toast.error('Please select an event');
      return;
    }
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    if (formData.payment_methods.length === 0) {
      toast.error('Please add at least one payment method');
      return;
    }
    
    const { difference } = calculateTotals();
    if (Math.abs(difference) > 0.01) {
      toast.error('Payment amounts must equal item totals');
      return;
    }

    try {
      await salesAPI.create({
        event_id: formData.event_id || null,
        sale_date: formData.sale_date,
        sale_type: formData.sale_type,
        sales_channel: formData.sales_channel,
        items: formData.items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price
        })),
        payment_methods: formData.payment_methods,
        channel_fees: formData.channel_fees,
        boutique_name: formData.boutique_name || null,
        order_number: formData.order_number || null,
        notes: formData.notes || null,
        deduct_inventory: formData.deduct_inventory,
      });
      toast.success('Sale recorded');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to record sale'));
    }
  };

  const resetForm = () => {
    setFormData({
      event_id: '',
      sale_date: new Date().toISOString().split('T')[0],
      sale_type: 'individual',
      sales_channel: 'event',
      items: [],
      payment_methods: [],
      channel_fees: [],
      boutique_name: '',
      order_number: '',
      notes: '',
      deduct_inventory: false,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sale record?')) return;
    
    try {
      await salesAPI.delete(id);
      toast.success('Sale deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete sale');
    }
  };

  // Apply filters
  let filteredSales = sales;
  if (eventFilter !== 'all') {
    filteredSales = filteredSales.filter(s => s.event_id === eventFilter);
  }
  if (channelFilter !== 'all') {
    filteredSales = filteredSales.filter(s => s.sales_channel === channelFilter);
  }
  // Date range filter
  if (dateRangeFilter.start) {
    filteredSales = filteredSales.filter(s => s.sale_date >= dateRangeFilter.start);
  }
  if (dateRangeFilter.end) {
    filteredSales = filteredSales.filter(s => s.sale_date <= dateRangeFilter.end);
  }
  // Product filter
  if (productFilter !== 'all') {
    filteredSales = filteredSales.filter(s => 
      s.items?.some(item => item.product_id === productFilter || item.product_name === productFilter)
    );
  }

  // Group by event if enabled
  const groupedSales = groupByEvent ? filteredSales.reduce((groups, sale) => {
    const eventKey = sale.event_id || sale.boutique_name || 'direct';
    const eventName = sale.event_title || sale.boutique_name || 'Direct Sales';
    if (!groups[eventKey]) {
      groups[eventKey] = {
        eventName,
        eventCity: sale.event_city,
        eventState: sale.event_state,
        sales: [],
        totalRevenue: 0,
        totalProfit: 0,
        totalItems: 0
      };
    }
    groups[eventKey].sales.push(sale);
    groups[eventKey].totalRevenue += sale.total_revenue || 0;
    groups[eventKey].totalProfit += sale.net_profit || sale.gross_profit || 0;
    groups[eventKey].totalItems += (sale.items || []).reduce((sum, item) => sum + (item.quantity || 0), 0);
    return groups;
  }, {}) : null;

  // Get unique products from all sales for filter
  const uniqueProducts = [...new Map(
    sales.flatMap(s => s.items || []).map(item => [item.product_id || item.product_name, item])
  ).values()];

  // Calculate summary stats - with safeguards for undefined values
  const totalRevenue = filteredSales.reduce((sum, s) => sum + (s.total_revenue || 0), 0);
  const totalFees = filteredSales.reduce((sum, s) => sum + (s.total_fees || 0), 0);
  const totalProfit = filteredSales.reduce((sum, s) => sum + (s.net_profit || s.gross_profit || 0), 0);
  const totalItems = filteredSales.reduce((sum, s) => 
    sum + (s.items || []).reduce((isum, item) => isum + (item.quantity || 0), 0), 0
  );

  // Get channel for display
  const getChannelConfig = (channel) => {
    // First check built-in channels
    const builtIn = BUILTIN_CHANNELS.find(c => c.value === channel);
    if (builtIn) {
      const colors = COLOR_CLASSES[builtIn.color] || COLOR_CLASSES.gray;
      return { ...builtIn, colorClasses: colors };
    }
    // Then check custom channels
    const custom = allChannels.find(c => c.value === channel);
    if (custom) {
      const colors = COLOR_CLASSES[custom.color] || COLOR_CLASSES.gray;
      return { ...custom, colorClasses: colors };
    }
    // Fallback
    return { value: channel, label: channel, icon: DollarSign, color: 'gray', colorClasses: COLOR_CLASSES.gray };
  };

  // Custom channel management functions
  const handleCreateChannel = async () => {
    if (!channelFormData.name.trim()) {
      toast.error('Channel name is required');
      return;
    }
    try {
      await customChannelsAPI.create({
        name: channelFormData.name,
        icon: channelFormData.icon,
        color: channelFormData.color,
        description: channelFormData.description,
        default_fees: channelFormData.default_fees,
      });
      toast.success('Channel created');
      setIsChannelDialogOpen(false);
      resetChannelForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create channel'));
    }
  };

  const handleUpdateChannel = async () => {
    if (!editingChannel) return;
    try {
      await customChannelsAPI.update(editingChannel.id, {
        name: channelFormData.name,
        icon: channelFormData.icon,
        color: channelFormData.color,
        description: channelFormData.description,
        default_fees: channelFormData.default_fees,
      });
      toast.success('Channel updated');
      setIsChannelDialogOpen(false);
      setEditingChannel(null);
      resetChannelForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update channel'));
    }
  };

  const handleDeleteChannel = async (channelId) => {
    if (!window.confirm('Delete this channel? This cannot be undone.')) return;
    try {
      await customChannelsAPI.delete(channelId);
      toast.success('Channel deleted');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete channel'));
    }
  };

  const openEditChannel = (channel) => {
    setEditingChannel(channel);
    setChannelFormData({
      name: channel.name,
      icon: channel.icon,
      color: channel.color,
      description: channel.description || '',
      default_fees: channel.default_fees || [],
    });
    setIsChannelDialogOpen(true);
  };

  const resetChannelForm = () => {
    setChannelFormData({ name: '', icon: 'star', color: 'gray', description: '', default_fees: [] });
    setNewFee({ description: '', amount: '', is_percentage: false });
  };

  const handleAddFee = () => {
    if (!newFee.description || !newFee.amount) {
      toast.error('Fee description and amount required');
      return;
    }
    setChannelFormData({
      ...channelFormData,
      default_fees: [...channelFormData.default_fees, {
        fee_type: 'custom',
        description: newFee.description,
        amount: parseFloat(newFee.amount),
        is_percentage: newFee.is_percentage,
      }]
    });
    setNewFee({ description: '', amount: '', is_percentage: false });
  };

  const handleRemoveFee = (index) => {
    setChannelFormData({
      ...channelFormData,
      default_fees: channelFormData.default_fees.filter((_, i) => i !== index)
    });
  };


  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FFFFFF] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-2">
            <div>
              <h1 className="text-3xl font-bold text-[#1E3A5F] font-['Outfit']">Sales</h1>
              <p className="text-gray-600 mt-1">Track sales across all your channels</p>
            </div>
            <HelpButton tutorialId="sales" />
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => { setEditingChannel(null); resetChannelForm(); setIsChannelDialogOpen(true); }} 
              variant="outline"
              className="border-[#1E3A5F] text-[#1E3A5F]"
              data-testid="manage-channels-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Manage Channels
            </Button>
            <Button 
              onClick={() => setIsDialogOpen(true)} 
              className="btn-primary"
              data-testid="record-sale-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Record Sale
            </Button>
          </div>
        </div>

        {/* Tabs for Sales and Etsy Orders */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="bg-white border-2 border-[#1E3A5F] rounded-xl p-1">
            <TabsTrigger 
              value="sales" 
              className="data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white rounded-lg px-4"
              data-testid="sales-tab"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              All Sales
            </TabsTrigger>
            <TabsTrigger 
              value="etsy" 
              className="data-[state=active]:bg-[#F56400] data-[state=active]:text-white rounded-lg px-4"
              data-testid="etsy-tab"
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              Etsy Orders
              {etsyStatus?.connected && etsyOrders.length > 0 && (
                <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                  {etsyOrders.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* All Sales Tab */}
          <TabsContent value="sales" className="mt-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
              <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Revenue</p>
                <p className="text-lg font-bold text-green-600">${totalRevenue.toFixed(2)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Percent className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Fees</p>
                <p className="text-lg font-bold text-red-600">-${totalFees.toFixed(2)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Net Profit</p>
                <p className="text-lg font-bold text-blue-600">${totalProfit.toFixed(2)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ShoppingCart className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Sales</p>
                <p className="text-lg font-bold text-purple-600">{filteredSales.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Package className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Items</p>
                <p className="text-lg font-bold text-orange-600">{totalItems}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 mb-6 shadow-hard">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm">Channel:</Label>
              <Select value={channelFilter} onValueChange={setChannelFilter}>
                <SelectTrigger className="w-44" data-testid="channel-filter">
                  <SelectValue placeholder="All Channels" />
                </SelectTrigger>
                <SelectContent className="bg-white border-2 border-[#1E3A5F]">
                  <SelectItem value="all">All Channels</SelectItem>
                  {allChannels.map((channel) => (
                    <SelectItem key={channel.value} value={channel.value}>
                      <div className="flex items-center gap-2">
                        <channel.icon className="w-4 h-4" />
                        {channel.label}
                        {channel.isCustom && <span className="text-xs text-gray-400">(Custom)</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">Event:</Label>
              <Select value={eventFilter} onValueChange={setEventFilter}>
                <SelectTrigger className="w-52" data-testid="event-filter">
                  <SelectValue placeholder="All Events" />
                </SelectTrigger>
                <SelectContent className="bg-white border-2 border-[#1E3A5F]">
                  <SelectItem value="all">All Events</SelectItem>
                  {events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={`border-[#1E3A5F] ${showFilters ? 'bg-[#1E3A5F] text-white' : 'text-[#1E3A5F]'}`}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? 'Hide Filters' : 'More Filters'}
            </Button>
            {/* Group by Event Toggle */}
            <div className="flex items-center gap-2 ml-auto">
              <Checkbox
                id="groupByEvent"
                checked={groupByEvent}
                onCheckedChange={setGroupByEvent}
                data-testid="group-by-event-checkbox"
              />
              <Label htmlFor="groupByEvent" className="text-sm cursor-pointer flex items-center gap-1">
                <Layers className="w-4 h-4" />
                Group by Event
              </Label>
            </div>
          </div>
          
          {/* Advanced Filters */}
          {showFilters && (
            <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <Label className="text-sm">Date Range:</Label>
                <Input
                  type="date"
                  value={dateRangeFilter.start}
                  onChange={(e) => setDateRangeFilter(prev => ({ ...prev, start: e.target.value }))}
                  className="w-40"
                  data-testid="date-start-filter"
                />
                <span className="text-gray-400">to</span>
                <Input
                  type="date"
                  value={dateRangeFilter.end}
                  onChange={(e) => setDateRangeFilter(prev => ({ ...prev, end: e.target.value }))}
                  className="w-40"
                  data-testid="date-end-filter"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Product:</Label>
                <Select value={productFilter} onValueChange={setProductFilter}>
                  <SelectTrigger className="w-48" data-testid="product-filter">
                    <SelectValue placeholder="All Products" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-2 border-[#1E3A5F]">
                    <SelectItem value="all">All Products</SelectItem>
                    {uniqueProducts.map((item) => (
                      <SelectItem key={item.product_id || item.product_name} value={item.product_id || item.product_name}>
                        {item.product_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDateRangeFilter({ start: '', end: '' });
                  setProductFilter('all');
                  setEventFilter('all');
                  setChannelFilter('all');
                  setGroupByEvent(false);
                }}
                className="text-gray-500 hover:text-red-500"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            </div>
          )}
        </div>

        {/* Sales List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E3A5F]"></div>
          </div>
        ) : filteredSales.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-12 text-center shadow-hard">
            <ShoppingCart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">No sales recorded</h3>
            <p className="text-gray-600 mb-4">Start tracking your event sales</p>
            <Button onClick={() => setIsDialogOpen(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Record Sale
            </Button>
          </div>
        ) : groupByEvent && groupedSales ? (
          /* Grouped by Event View */
          <div className="space-y-6">
            {Object.entries(groupedSales).map(([eventKey, group]) => (
              <div key={eventKey} className="bg-white rounded-xl border-2 border-[#1E3A5F] shadow-hard overflow-hidden">
                {/* Group Header */}
                <div className="bg-[#1E3A5F]/10 p-4 border-b-2 border-[#1E3A5F]">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-[#1E3A5F] flex items-center gap-2">
                        <Layers className="w-5 h-5" />
                        {group.eventName}
                      </h3>
                      {(group.eventCity || group.eventState) && (
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {[group.eventCity, group.eventState].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-6">
                        <div>
                          <p className="text-xs text-gray-500">Transactions</p>
                          <p className="text-lg font-bold text-purple-600">{group.sales.length}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Items Sold</p>
                          <p className="text-lg font-bold text-orange-600">{group.totalItems}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Revenue</p>
                          <p className="text-lg font-bold text-green-600">${group.totalRevenue.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Net Profit</p>
                          <p className={`text-lg font-bold ${group.totalProfit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                            ${group.totalProfit.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Group Sales */}
                <div className="divide-y divide-gray-100">
                  {group.sales.map((sale) => {
                    const channelConfig = getChannelConfig(sale.sales_channel);
                    const ChannelIcon = channelConfig.icon;
                    const badgeClasses = `${channelConfig.colorClasses?.bg || 'bg-gray-100'} ${channelConfig.colorClasses?.text || 'text-gray-700'}`;
                    
                    return (
                      <div key={sale.id} className="p-4 hover:bg-gray-50" data-testid={`sale-${sale.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-lg ${badgeClasses}`}>
                              <ChannelIcon className="w-3 h-3" />
                              {channelConfig.label}
                            </span>
                            <span className="text-sm text-gray-500 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(sale.sale_date)}
                            </span>
                            <div className="flex flex-wrap gap-1">
                              {sale.items.slice(0, 3).map((item, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-[#FFEDDF] text-[#1E3A5F] text-xs rounded">
                                  {item.quantity}x {item.product_name}
                                </span>
                              ))}
                              {sale.items.length > 3 && (
                                <span className="text-xs text-gray-400">+{sale.items.length - 3} more</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="font-semibold text-green-600">${(sale.total_revenue || 0).toFixed(2)}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(sale.id)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Regular List View */
          <div className="space-y-4">
            {filteredSales.map((sale) => {
              const channelConfig = getChannelConfig(sale.sales_channel);
              const ChannelIcon = channelConfig.icon;
              const hasFees = sale.total_fees > 0;
              const badgeClasses = `${channelConfig.colorClasses?.bg || 'bg-gray-100'} ${channelConfig.colorClasses?.text || 'text-gray-700'}`;
              
              return (
                <div
                  key={sale.id}
                  className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard"
                  data-testid={`sale-${sale.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      {/* Header: Event Name & Channel */}
                      <div className="mb-3 pb-3 border-b border-gray-100">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-[#1E3A5F]" data-testid={`sale-event-name-${sale.id}`}>
                              {sale.event_title || sale.boutique_name || 'Direct Sale'}
                            </h3>
                            {(sale.event_city || sale.event_state) && (
                              <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                                <MapPin className="w-3 h-3" />
                                {[sale.event_city, sale.event_state].filter(Boolean).join(', ')}
                              </p>
                            )}
                          </div>
                          {sale.order_number && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded font-mono">
                              #{sale.order_number}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          {/* Channel Badge - More Prominent */}
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg ${badgeClasses}`} data-testid={`sale-channel-${sale.id}`}>
                            <ChannelIcon className="w-4 h-4" />
                            {channelConfig.label}
                          </span>
                          <span className="flex items-center gap-1 text-sm text-gray-500">
                            <Calendar className="w-4 h-4" />
                            {formatDate(sale.sale_date)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Items */}
                      <div className="mb-3">
                        <p className="text-xs text-gray-500 mb-1 font-medium">Items Sold:</p>
                        <div className="flex flex-wrap gap-2">
                          {sale.items.map((item, idx) => (
                            <span key={idx} className="px-2 py-1 bg-[#FFEDDF] text-[#1E3A5F] text-sm rounded font-medium">
                              {item.quantity}x {item.product_name} @ ${item.unit_price.toFixed(2)}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {/* Payment Methods */}
                      <div>
                        <p className="text-xs text-gray-500 mb-1 font-medium">Payment:</p>
                        <div className="flex flex-wrap gap-2">
                          {sale.payment_methods.map((pm, idx) => (
                            <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded capitalize">
                              {pm.method}: ${pm.amount.toFixed(2)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right ml-4 min-w-[120px]">
                      <p className="text-2xl font-bold text-green-600" data-testid={`sale-revenue-${sale.id}`}>${(sale.total_revenue || 0).toFixed(2)}</p>
                      
                      {/* Fees breakdown */}
                      {hasFees && (
                        <div className="text-xs text-red-500 flex items-center justify-end gap-1" data-testid={`sale-fees-${sale.id}`}>
                          <Percent className="w-3 h-3" />
                          Fees: -${(sale.total_fees || 0).toFixed(2)}
                        </div>
                      )}
                      
                      {/* Net Profit */}
                      <p className="text-sm mt-1" data-testid={`sale-profit-${sale.id}`}>
                        <span className="text-gray-500">Net: </span>
                        <span className={`font-semibold ${(sale.net_profit || sale.gross_profit || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          ${(sale.net_profit || sale.gross_profit || 0).toFixed(2)}
                        </span>
                      </p>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(sale.id)}
                        className="text-red-600 hover:bg-red-50 mt-2"
                        data-testid={`delete-sale-${sale.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
          </TabsContent>

          {/* Etsy Orders Tab */}
          <TabsContent value="etsy" className="mt-6">
            {etsyLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#F56400]" />
              </div>
            ) : !etsyStatus?.connected ? (
              <div className="bg-white rounded-xl border-2 border-[#F56400] p-12 text-center shadow-hard">
                <ShoppingBag className="w-16 h-16 mx-auto text-[#F56400]/30 mb-4" />
                <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">Connect Your Etsy Shop</h3>
                <p className="text-gray-600 mb-4">
                  Link your Etsy account to automatically import orders and track fees
                </p>
                <Button 
                  onClick={() => navigate('/settings')} 
                  className="bg-[#F56400] hover:bg-[#d45500] text-white"
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Connect Etsy in Settings
                </Button>
              </div>
            ) : (
              <>
                {/* Etsy Summary Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-xl border-2 border-[#F56400] p-4 shadow-hard">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <DollarSign className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Revenue</p>
                        <p className="text-lg font-bold text-green-600">
                          ${(etsySummary?.total_revenue || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border-2 border-[#F56400] p-4 shadow-hard">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <Percent className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Total Fees</p>
                        <p className="text-lg font-bold text-red-600">
                          -${(etsySummary?.total_fees || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border-2 border-[#F56400] p-4 shadow-hard">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Net Revenue</p>
                        <p className="text-lg font-bold text-blue-600">
                          ${(etsySummary?.net_revenue || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border-2 border-[#F56400] p-4 shadow-hard">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <Package className="w-5 h-5 text-[#F56400]" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Orders</p>
                        <p className="text-lg font-bold text-[#F56400]">
                          {etsySummary?.total_orders || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sync Button and Shop Info */}
                <div className="bg-white rounded-xl border-2 border-[#F56400] p-4 mb-6 shadow-hard">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#F56400] rounded-lg flex items-center justify-center">
                        <ShoppingBag className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1E3A5F]">{etsyStatus.shop_name || 'Your Etsy Shop'}</p>
                        <p className="text-xs text-gray-500">
                          Last sync: {etsyStatus.last_sync ? new Date(etsyStatus.last_sync).toLocaleString() : 'Never'}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleSyncEtsy}
                      disabled={etsySyncing}
                      className="bg-[#F56400] hover:bg-[#d45500] text-white"
                      data-testid="sync-etsy-orders-btn"
                    >
                      {etsySyncing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Sync Orders
                    </Button>
                  </div>
                </div>

                {/* Etsy Orders List */}
                {etsyOrders.length === 0 ? (
                  <div className="bg-white rounded-xl border-2 border-[#F56400] p-12 text-center shadow-hard">
                    <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">No orders synced yet</h3>
                    <p className="text-gray-600 mb-4">Click "Sync Orders" to import your Etsy orders</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {etsyOrders.map((order) => (
                      <div
                        key={order.id}
                        className="bg-white rounded-xl border-2 border-[#F56400] p-4 shadow-hard"
                        data-testid={`etsy-order-${order.receipt_id}`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="px-2 py-1 bg-orange-100 text-[#F56400] text-xs font-medium rounded-full">
                                #{order.receipt_id}
                              </span>
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                {order.status}
                              </span>
                            </div>
                            
                            <p className="text-sm text-gray-600 mb-2">
                              <span className="font-medium">{order.buyer_name || 'Buyer'}</span>
                              {' • '}
                              {order.created_at ? formatDate(order.created_at) : 'Unknown date'}
                            </p>
                            
                            {/* Items */}
                            <div className="space-y-1 mb-3">
                              {order.items?.map((item, idx) => (
                                <div key={idx} className="flex items-center justify-between text-sm">
                                  <span className="text-gray-700 truncate max-w-xs">{item.title}</span>
                                  <span className="text-gray-500">
                                    {item.quantity}x ${item.price?.toFixed(2)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          <div className="text-right space-y-2">
                            <div>
                              <p className="text-sm text-gray-500">Total</p>
                              <p className="text-xl font-bold text-[#1E3A5F]">
                                ${order.grand_total?.toFixed(2)}
                              </p>
                            </div>
                            
                            {/* Fees breakdown */}
                            <div className="text-xs space-y-1 bg-red-50 rounded-lg p-2">
                              <p className="text-red-600 font-medium">Etsy Fees:</p>
                              <p className="text-red-500">Listing: -${order.fees?.listing_fee?.toFixed(2)}</p>
                              <p className="text-red-500">Transaction: -${order.fees?.transaction_fee?.toFixed(2)}</p>
                              <p className="text-red-500">Processing: -${order.fees?.processing_fee?.toFixed(2)}</p>
                              <p className="text-red-700 font-semibold pt-1 border-t border-red-200">
                                Total: -${order.fees?.total_fees?.toFixed(2)}
                              </p>
                            </div>
                            
                            <div className="pt-2">
                              <p className="text-xs text-gray-500">Net</p>
                              <p className="text-lg font-bold text-green-600">
                                ${(order.grand_total - (order.fees?.total_fees || 0)).toFixed(2)}
                              </p>
                            </div>
                            
                            <Button
                              size="sm"
                              onClick={() => handleImportEtsyOrder(order.id)}
                              disabled={importingOrderId === order.id}
                              className="w-full bg-[#1E3A5F] hover:bg-[#15305f] text-white"
                              data-testid={`import-order-${order.receipt_id}`}
                            >
                              {importingOrderId === order.id ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4 mr-1" />
                              )}
                              Import to Sales
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Record Sale Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F]">Record Sale</DialogTitle>
            <DialogDescription>
              Log a sale with products and payment details
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Sales Channel Selection */}
            <div>
              <Label className="text-lg font-semibold text-[#1E3A5F]">Sales Channel *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                {allChannels.map((channel) => {
                  const Icon = channel.icon;
                  const isSelected = formData.sales_channel === channel.value;
                  return (
                    <button
                      key={channel.value}
                      type="button"
                      onClick={() => handleChannelChange(channel.value)}
                      className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                        isSelected 
                          ? 'border-[#1E3A5F] bg-[#FFEDDF]' 
                          : 'border-gray-200 hover:border-gray-300'
                      } ${channel.isCustom ? 'border-dashed' : ''}`}
                      data-testid={`channel-${channel.value}`}
                    >
                      <Icon className={`w-5 h-5 mb-1 ${isSelected ? 'text-[#1E3A5F]' : 'text-gray-500'}`} />
                      <span className={`text-xs text-center ${isSelected ? 'text-[#1E3A5F] font-medium' : 'text-gray-600'}`}>
                        {channel.label}
                      </span>
                      {channel.isCustom && <span className="text-[8px] text-gray-400 mt-0.5">Custom</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Event Selection (only for event-based channels) */}
            {['event', 'farmers_market'].includes(formData.sales_channel) && (
              <div className="space-y-4">
                {/* Series Event Selector - Shows when series events are available */}
                {seriesEvents.length > 0 && (
                  <div className="p-4 bg-[#1E3A5F]/10 rounded-xl border border-[#1E3A5F]/30">
                    <div className="flex items-center gap-2 mb-3">
                      <History className="w-5 h-5 text-[#1E3A5F]" />
                      <Label className="text-[#1E3A5F] font-semibold">
                        Select Event in "{seriesName}" Series
                      </Label>
                    </div>
                    <Select
                      value={formData.event_id}
                      onValueChange={(value) => setFormData({...formData, event_id: value})}
                    >
                      <SelectTrigger data-testid="sale-series-event-select" className="bg-white">
                        <SelectValue placeholder="Select which event to assign this sale" />
                      </SelectTrigger>
                      <SelectContent>
                        {seriesEvents.map((occ) => (
                          <SelectItem key={occ.event_id} value={occ.event_id}>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-[#1E3A5F]">
                                {occ.date ? new Date(occ.date).getFullYear() : 'N/A'}
                              </span>
                              <span>-</span>
                              <span>{occ.title}</span>
                              {occ.city && (
                                <span className="text-gray-400 text-sm">
                                  ({occ.city}, {occ.state})
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-2">
                      Choose which year's event this sale should be recorded under
                    </p>
                  </div>
                )}
                
                {/* Standard Event Selector - Shows when no series events or as fallback */}
                {seriesEvents.length === 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Event *</Label>
                      <Select
                        value={formData.event_id}
                        onValueChange={(value) => setFormData({...formData, event_id: value})}
                      >
                        <SelectTrigger data-testid="sale-event-select">
                          <SelectValue placeholder="Select event" />
                        </SelectTrigger>
                        <SelectContent>
                          {events.map((event) => (
                            <SelectItem key={event.id} value={event.id}>
                              {event.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Sale Date *</Label>
                      <Input
                        type="date"
                        value={formData.sale_date}
                        onChange={(e) => setFormData({...formData, sale_date: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                )}
                
                {/* Sale Date - Only show separately when series events are shown */}
                {seriesEvents.length > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    <div></div>
                    <div>
                      <Label>Sale Date *</Label>
                      <Input
                        type="date"
                        value={formData.sale_date}
                        onChange={(e) => setFormData({...formData, sale_date: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Boutique/Consignment Details */}
            {formData.sales_channel === 'boutique' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Boutique/Store Name</Label>
                  <Input
                    value={formData.boutique_name}
                    onChange={(e) => setFormData({...formData, boutique_name: e.target.value})}
                    placeholder="e.g., The Craft Shop"
                  />
                </div>
                <div>
                  <Label>Sale Date *</Label>
                  <Input
                    type="date"
                    value={formData.sale_date}
                    onChange={(e) => setFormData({...formData, sale_date: e.target.value})}
                    required
                  />
                </div>
              </div>
            )}

            {/* Etsy/Website Order Details */}
            {['etsy', 'website', 'wholesale'].includes(formData.sales_channel) && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Order Number</Label>
                  <Input
                    value={formData.order_number}
                    onChange={(e) => setFormData({...formData, order_number: e.target.value})}
                    placeholder="e.g., 12345678"
                  />
                </div>
                <div>
                  <Label>Sale Date *</Label>
                  <Input
                    type="date"
                    value={formData.sale_date}
                    onChange={(e) => setFormData({...formData, sale_date: e.target.value})}
                    required
                  />
                </div>
              </div>
            )}

            {/* Other channel - just date */}
            {!['event', 'farmers_market', 'boutique', 'etsy', 'website', 'wholesale'].includes(formData.sales_channel) && (
              <div>
                <Label>Sale Date *</Label>
                <Input
                  type="date"
                  value={formData.sale_date}
                  onChange={(e) => setFormData({...formData, sale_date: e.target.value})}
                  required
                  className="max-w-xs"
                />
              </div>
            )}

            {/* Channel Fees */}
            {formData.channel_fees.length > 0 && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <Percent className="w-4 h-4 text-amber-600" />
                  <Label className="text-amber-700 font-medium">Channel Fees</Label>
                </div>
                <div className="space-y-1">
                  {formData.channel_fees.map((fee, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-amber-700">{fee.description}</span>
                      <span className="text-amber-800 font-mono">
                        {fee.is_percentage 
                          ? `${fee.amount}% = -$${(calculateTotals().itemsTotal * fee.amount / 100).toFixed(2)}`
                          : `-$${fee.amount.toFixed(2)}`
                        }
                      </span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-semibold pt-1 border-t border-amber-300">
                    <span className="text-amber-700">Total Fees</span>
                    <span className="text-red-600">-${calculateTotals().feesTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Sale Type */}
            <div>
              <Label>Sale Type</Label>
              <Select
                value={formData.sale_type}
                onValueChange={(value) => setFormData({...formData, sale_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual Transaction</SelectItem>
                  <SelectItem value="batch">Batch Entry (End of Event)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-lg font-semibold text-[#1E3A5F]">Items Sold</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowQuickAddProduct(true)}
                  className="text-[#1E3A5F] h-8 text-sm"
                  data-testid="quick-add-product-btn"
                >
                  <PlusCircle className="w-4 h-4 mr-1" /> New Product
                </Button>
              </div>
              <div className="flex gap-2 mt-2 mb-3">
                <Select
                  value={newItem.product_id}
                  onValueChange={(value) => {
                    const product = products.find(p => p.id === value);
                    setNewItem({
                      ...newItem, 
                      product_id: value,
                      unit_price: product?.retail_price?.toString() || ''
                    });
                  }}
                >
                  <SelectTrigger className="flex-1" data-testid="item-product-select">
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name} (${product.retail_price?.toFixed(2) || '0.00'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min="1"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({...newItem, quantity: e.target.value})}
                  placeholder="Qty"
                  className="w-20"
                />
                <Input
                  type="number"
                  step="0.01"
                  value={newItem.unit_price}
                  onChange={(e) => setNewItem({...newItem, unit_price: e.target.value})}
                  placeholder="Price"
                  className="w-24"
                />
                <Button type="button" onClick={handleAddItem} variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {formData.items.length > 0 ? (
                <div className="border rounded-lg divide-y">
                  {formData.items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2">
                      <div>
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-sm text-gray-500">
                          {item.quantity} × ${item.unit_price.toFixed(2)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-semibold">
                          ${(item.quantity * item.unit_price).toFixed(2)}
                        </span>
                        <button type="button" onClick={() => handleRemoveItem(idx)} className="text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between p-2 bg-[#FFEDDF]">
                    <span className="font-semibold">Items Total</span>
                    <span className="font-mono font-bold text-[#1E3A5F]">
                      ${calculateTotals().itemsTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-center py-4 text-gray-500 border rounded-lg">No items added</p>
              )}
            </div>

            {/* Payment Methods */}
            <div>
              <Label className="text-lg font-semibold text-[#1E3A5F]">Payment Methods</Label>
              <div className="flex gap-2 mt-2 mb-3">
                <Select
                  value={newPayment.method}
                  onValueChange={(value) => setNewPayment({...newPayment, method: value})}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((pm) => (
                      <SelectItem key={pm.value} value={pm.value}>
                        {pm.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  step="0.01"
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                  placeholder="Amount"
                  className="w-32"
                />
                <Button type="button" onClick={handleAddPayment} variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {formData.payment_methods.length > 0 ? (
                <div className="border rounded-lg divide-y">
                  {formData.payment_methods.map((pm, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2">
                      <span className="capitalize">{pm.method}</span>
                      <div className="flex items-center gap-3">
                        <span className="font-mono">${pm.amount.toFixed(2)}</span>
                        <button type="button" onClick={() => handleRemovePayment(idx)} className="text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between p-2 bg-gray-100">
                    <span className="font-semibold">Payments Total</span>
                    <span className="font-mono font-bold">
                      ${calculateTotals().paymentsTotal.toFixed(2)}
                    </span>
                  </div>
                  {Math.abs(calculateTotals().difference) > 0.01 && (
                    <div className="p-2 bg-red-100 text-red-700 text-sm">
                      Difference: ${calculateTotals().difference.toFixed(2)} - must be $0.00
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-center py-4 text-gray-500 border rounded-lg">No payments added</p>
              )}
            </div>

            {/* Inventory Deduction */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Checkbox
                id="deduct_inventory"
                checked={formData.deduct_inventory}
                onCheckedChange={(checked) => setFormData({...formData, deduct_inventory: checked})}
              />
              <div>
                <Label htmlFor="deduct_inventory" className="cursor-pointer">
                  Deduct from inventory
                </Label>
                <p className="text-xs text-gray-500">
                  Automatically reduce material stock based on products sold
                </p>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Optional notes..."
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="submit-sale-btn">
                Record Sale
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Custom Channels Dialog */}
      <Dialog open={isChannelDialogOpen} onOpenChange={setIsChannelDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F] font-['Outfit']">
              {editingChannel ? 'Edit Channel' : 'Manage Sales Channels'}
            </DialogTitle>
            <DialogDescription>
              {editingChannel 
                ? 'Update your custom sales channel' 
                : `Create custom channels for workshops, custom orders, or any other sales source. (${customChannels.length}/99 created)`
              }
            </DialogDescription>
          </DialogHeader>

          {!editingChannel && (
            <>
              {/* List of Custom Channels */}
              {customChannels.length > 0 && (
                <div className="space-y-2 mb-6">
                  <Label className="text-sm font-medium text-gray-700">Your Custom Channels</Label>
                  <div className="border rounded-lg divide-y max-h-40 overflow-y-auto">
                    {customChannels.map((ch) => {
                      const Icon = ICON_MAP[ch.icon] || Star;
                      const colors = COLOR_CLASSES[ch.color] || COLOR_CLASSES.gray;
                      return (
                        <div key={ch.id} className="flex items-center justify-between p-3">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${colors.iconBg}`}>
                              <Icon className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <p className="font-medium text-[#1E3A5F]">{ch.name}</p>
                              <p className="text-xs text-gray-500">
                                {ch.default_fees?.length > 0 
                                  ? `${ch.default_fees.length} fee(s) configured` 
                                  : 'No default fees'}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => openEditChannel(ch)}
                              data-testid={`edit-channel-${ch.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteChannel(ch.id)}
                              data-testid={`delete-channel-${ch.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <Label className="text-sm font-medium text-gray-700">Create New Channel</Label>
              </div>
            </>
          )}

          {/* Channel Form */}
          <div className="space-y-4">
            <div>
              <Label>Channel Name *</Label>
              <Input
                value={channelFormData.name}
                onChange={(e) => setChannelFormData({...channelFormData, name: e.target.value})}
                placeholder="e.g., Workshops, Custom Orders"
                data-testid="channel-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Icon</Label>
                <div className="grid grid-cols-6 gap-1 p-2 border rounded-lg max-h-32 overflow-y-auto">
                  {Object.entries(ICON_MAP).map(([key, Icon]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setChannelFormData({...channelFormData, icon: key})}
                      className={`p-2 rounded transition-all ${
                        channelFormData.icon === key 
                          ? 'bg-[#1E3A5F] text-white' 
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Color</Label>
                <div className="grid grid-cols-5 gap-1 p-2 border rounded-lg">
                  {Object.keys(COLOR_CLASSES).map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setChannelFormData({...channelFormData, color})}
                      className={`p-3 rounded transition-all ${COLOR_CLASSES[color].iconBg} ${
                        channelFormData.color === color 
                          ? 'ring-2 ring-[#1E3A5F] ring-offset-2' 
                          : ''
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label>Description (Optional)</Label>
              <Input
                value={channelFormData.description}
                onChange={(e) => setChannelFormData({...channelFormData, description: e.target.value})}
                placeholder="e.g., Teaching pottery classes"
              />
            </div>

            {/* Default Fees Section */}
            <div>
              <Label className="flex items-center gap-2">
                <Percent className="w-4 h-4" />
                Default Fees (Optional)
              </Label>
              <p className="text-xs text-gray-500 mb-2">
                Fees will be automatically applied when recording sales from this channel
              </p>

              {channelFormData.default_fees.length > 0 && (
                <div className="border rounded-lg divide-y mb-3">
                  {channelFormData.default_fees.map((fee, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2">
                      <span className="text-sm">{fee.description}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono text-amber-600">
                          {fee.is_percentage ? `${fee.amount}%` : `$${fee.amount.toFixed(2)}`}
                        </span>
                        <button type="button" onClick={() => handleRemoveFee(idx)} className="text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  value={newFee.description}
                  onChange={(e) => setNewFee({...newFee, description: e.target.value})}
                  placeholder="Fee description"
                  className="flex-1"
                />
                <Input
                  type="number"
                  step="0.01"
                  value={newFee.amount}
                  onChange={(e) => setNewFee({...newFee, amount: e.target.value})}
                  placeholder="Amount"
                  className="w-24"
                />
                <Select
                  value={newFee.is_percentage ? 'percent' : 'fixed'}
                  onValueChange={(v) => setNewFee({...newFee, is_percentage: v === 'percent'})}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">$</SelectItem>
                    <SelectItem value="percent">%</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="button" onClick={handleAddFee} variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setIsChannelDialogOpen(false); setEditingChannel(null); resetChannelForm(); }}
            >
              Cancel
            </Button>
            <Button 
              className="btn-primary"
              onClick={editingChannel ? handleUpdateChannel : handleCreateChannel}
              data-testid="save-channel-btn"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              {editingChannel ? 'Update Channel' : 'Create Channel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Add Product Dialog */}
      <QuickAddProduct
        open={showQuickAddProduct}
        onOpenChange={setShowQuickAddProduct}
        onProductCreated={handleQuickProductCreated}
      />
    </div>
  );
};

export default Sales;
